import styled from 'styled-components';
import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiMenu, mdiClose, mdiWeatherSunny, mdiWeatherNight } from '@mdi/js';
import { useTheme } from '../../context/ThemeContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { Container } from '../ui/Container';
import { Button } from '../ui/Button';
// Button imported for MobileCTA usage

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-sticky);
  padding: var(--space-4) 0;
  background: var(--glass-background);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border-bottom: 1px solid var(--glass-border);
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-weight: var(--font-bold);
  font-size: var(--text-lg);
  color: var(--color-text-primary);
  text-decoration: none;

  &:hover {
    color: var(--color-text-primary);
  }
`;

const LogoIcon = styled.div`
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-secondary));
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: var(--font-bold);
  font-size: var(--text-sm);
`;

const LogoText = styled.span`
  @media (max-width: 480px) {
    display: none;
  }
`;

const Nav = styled.nav<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--space-2);

  @media (max-width: 768px) {
    position: fixed;
    top: 73px;
    left: 0;
    right: 0;
    bottom: 0;
    flex-direction: column;
    padding: var(--space-6);
    background: var(--glass-background-elevated);
    backdrop-filter: var(--glass-blur);
    -webkit-backdrop-filter: var(--glass-blur);
    transform: translateX(${props => props.$isOpen ? '0' : '100%'});
    transition: transform var(--transition-slow);
    gap: var(--space-2);
  }
`;

const NavItem = styled(NavLink)`
  padding: var(--space-2) var(--space-4);
  color: var(--color-text-secondary);
  text-decoration: none;
  font-weight: var(--font-medium);
  border-radius: var(--radius-md);
  transition: color var(--transition-fast), background var(--transition-fast);

  &:hover {
    color: var(--color-text-primary);
    background: var(--color-border);
  }

  &.active {
    color: var(--color-brand-primary);
    background: rgba(81, 20, 97, 0.1);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: var(--space-4);
    font-size: var(--text-lg);
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const ThemeToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background: var(--color-border);
  color: var(--color-text-secondary);
  transition: background var(--transition-fast), color var(--transition-fast);

  &:hover {
    background: var(--color-border-strong);
    color: var(--color-text-primary);
  }
`;

const MenuToggle = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  color: var(--color-text-primary);

  @media (max-width: 768px) {
    display: flex;
  }
`;

const CTALink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-2) var(--space-4);
  font-size: var(--text-sm);
  font-family: var(--font-sans);
  font-weight: var(--font-semibold);
  text-decoration: none;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-secondary));
  color: white;
  box-shadow: 0 4px 14px rgba(81, 20, 97, 0.25);
  transition: all var(--transition-base);

  &:hover {
    box-shadow: 0 6px 20px rgba(81, 20, 97, 0.35);
    transform: translateY(-2px);
    color: white;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileCTA = styled.div`
  display: none;
  width: 100%;
  margin-top: auto;
  padding-top: var(--space-6);

  @media (max-width: 768px) {
    display: block;
  }
`;

const navItems = [
  { to: '/', label: 'Home' },
  { to: '/products', label: 'Products' },
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/services', label: 'Services' },
  { to: '/about', label: 'About' },
];

export function Header() {
  const { theme, toggleTheme } = useTheme();
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <HeaderWrapper>
      <Container>
        <HeaderContent>
          <Logo to="/" onClick={closeMenu}>
            <LogoIcon>LSA</LogoIcon>
            <LogoText>Lancaster Systems Automation</LogoText>
          </Logo>

          <Nav $isOpen={isMenuOpen}>
            {navItems.map(item => (
              <NavItem
                key={item.to}
                to={item.to}
                onClick={closeMenu}
                end={item.to === '/'}
              >
                {item.label}
              </NavItem>
            ))}

            <MobileCTA onClick={closeMenu}>
              <Button as={Link} to="/contact" fullWidth>
                Contact Us
              </Button>
            </MobileCTA>
          </Nav>

          <Actions>
            <ThemeToggle onClick={toggleTheme} aria-label="Toggle theme">
              <Icon
                path={theme === 'light' ? mdiWeatherNight : mdiWeatherSunny}
                size={0.9}
              />
            </ThemeToggle>

            <CTALink to="/contact">
              Contact
            </CTALink>

            {isMobile && (
              <MenuToggle
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              >
                <Icon path={isMenuOpen ? mdiClose : mdiMenu} size={1} />
              </MenuToggle>
            )}
          </Actions>
        </HeaderContent>
      </Container>
    </HeaderWrapper>
  );
}
